<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Trash03Icon
} from 'front-library'
import {
  getProcessRequests,
  deleteProcessRequest
} from '@/app/core/services/pending-processes'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref()
const acceptQuestion = ref()

const loading = ref(false)

/* Datos */
const search = ref()
const tableProcessRequests = ref(null)
const processRequestForDelete = ref()

/* Datos componente table */
const columns = ref([
  {
    name: 'name',
    title: t('pendingProcesses.list.process'),
    table: 'processes.name'
  },
  {
    name: 'payroll_status',
    title: t('pendingProcesses.list.status'),
    table: 'payroll_status.name'
  },
  {
    name: 'status_process',
    title: t('pendingProcesses.list.processStatus'),
    table: 'process_status.name'
  },
  {
    name: 'employees_amount',
    title: t('pendingProcesses.list.employeeCount'),
    table: 'process_requests.employees_amount'
  },
  {
    name: 'concepts_amount',
    title: t('pendingProcesses.list.conceptCount'),
    table: 'process_requests.concepts_amount'
  },
  {
    name: 'start_date',
    title: t('pendingProcesses.list.startDate'),
    table: 'process_requests.start_date'
  },
  {
    name: 'end_date',
    title: t('pendingProcesses.list.endDate'),
    table: 'process_requests.end_date'
  },
  {
    name: 'user',
    title: t('pendingProcesses.list.user'),
    table: 'process_requests.user'
  },
  {
    name: 'updated_at',
    title: t('pendingProcesses.list.lastUpdate'),
    table: 'process_requests.updated_at'
  },

  { name: 'actions', title: '' }
])
const rows = ref<[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Eliminar Proceso pendiente */
const removeProcessRequest = (values: any) => {
  showModal.value = true
  titleModal.value = t('pendingProcesses.list.actionStatus', {
    status: t('table.action.delete')
  })
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  processRequestForDelete.value = values.id
}
const deleteConfirm = async () => {
  loading.value = true
  showModal.value = false
  await deleteProcessRequest(processRequestForDelete.value)
    .then((response: any) => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('pendingProcesses.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void processRequests()
    })
    .catch((error: any) => {
      notificationError(error, 'pendingProcesses', t, notifications, loading)
    })
}

const sheetPayroll = (id: any, idPendingProcesses: string) => {
  const type: string = 'premovements'
  void router.push({
    name: 'DetailPayrollSheet',
    params: { id, type, idPendingProcesses }
  })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar procesos pendientes */
const processRequests = async () => {
  loading.value = true
  try {
    const data: any = await getProcessRequests(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'pendingProcesses', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="deleteConfirm" />
  <div class="px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('pendingProcesses.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('pendingProcesses.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="processRequests" />
          <DBtn
            @click.prevent="processRequests"
            v-html="t('form.button.search')" />
        </div>
      </div>
    </div>
    <DTable
      ref="tableProcessRequests"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), processRequests()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), processRequests()">
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="
              store.hasPermission('payroll_closed:create') &&
              data.processed_payroll_id
            "
            arrow
            offsetDistance="12"
            :content="t('pendingProcesses.list.viewCalculation')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="sheetPayroll(data.processed_payroll_id, data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeProcessRequest(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
      <template #status_process="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.status === 'ERROR'
              ? 'bg-red-100 text-red-600'
              : data.status === 'COMPLETE'
              ? 'bg-green-100 text-green-600'
              : data.status === 'PENDING'
              ? 'bg-blue-100 text-blue-600'
              : 'bg-gray-100 text-gray-600'
          ">
          {{ t('status.' + data.status) }}
        </div>
      </template>
    </DTable>
  </div>
</template>
